import React from "react";

import classnames from "classnames";
import { calculateMaxEstimatedCost } from "ComparePlans/calculateCost";
import { calculatePlanDeviationProps } from "QuotePage/Selectors";
import formatCurrency from "Utils/formatCurrency";

import ColoredDot from "Common/ColoredDot";
import { finePrintLabels, splitHospitalFinePrintSections } from "Common/finePrintSections";
import { HeaderRow, ContentRow } from "Common/Table";

import type { MedicalPlanPrice } from "Utils/types";

import makeBem from "Utils/makeBem";

const bem = makeBem("EstimatedCosts");

type Props = {
  plans: Array<MedicalPlanPrice>;
  planDeviationProps?: Record<string, any>;
  heading?: React.ReactNode;
};

export default class EstimatedCosts extends React.Component<Props> {
  renderCostCell(plan: MedicalPlanPrice, serviceType: string, planDeviationProps: any) {
    const devKey = /^drug_tier:/.test(serviceType) ? "brand_drugs" : serviceType;
    if (typeof plan.estimated_costs[serviceType] === "undefined") {
      return null;
    }

    return [
      <ColoredDot
        key="dot"
        value={plan.estimated_costs[serviceType]}
        mean={planDeviationProps[`mean_${devKey}`]}
        stdev={planDeviationProps[`stdev_${devKey}`]}
      />,

      <span key="value"> {formatCurrency(calculateMaxEstimatedCost(plan, serviceType))}</span>,
    ];
  }

  render() {
    const { plans } = this.props;
    let { planDeviationProps } = this.props;

    // This is necessary because this gets used in the employee side,
    // which will not have access to all plans, and thus needs to calculate
    // based on what it has.
    if (planDeviationProps === undefined || planDeviationProps === null) {
      planDeviationProps = calculatePlanDeviationProps(plans);
    }

    const rows = finePrintLabels.map(([key, label], i) => {
      const sectionLabel = <HeaderRow heading={label} className={classnames(bem("headerRow"))} key={i} items={plans} />;

      const dontShowSplit = {
        hospitalization_physician: true,
        surgery_staff: true,
        labor: true,
      };

      const sectionRows = splitHospitalFinePrintSections[key].map((config) => {
        let usedLabel = config.label;

        if (usedLabel == "Drug Deductible") {
          return null;
        }

        if (dontShowSplit.hasOwnProperty(config.key)) {
          return null;
        }
        usedLabel = usedLabel.split(" - ")[0];

        return (
          <ContentRow
            className={classnames(bem("contentRow"))}
            heading={usedLabel}
            cell={(p) => this.renderCostCell(p, config.key, planDeviationProps)}
            key={`${i}:${config.key}`}
            items={plans}
          />
        );
      });
      return [sectionLabel].concat(sectionRows);
    });
    return (
      <tbody className={classnames(bem())}>
        <HeaderRow className={classnames(bem("mainHeader"))} heading={this.props.heading || "Estimated Costs"} items={plans} />

        <tr>
          <td colSpan={plans.length + 1} style={{ textAlign: "center", backgroundColor: "#f7f7f7" }}>
            <span style={{ fontWeight: 600 }}>OUT OF POCKET COST: </span>
            <div className={classnames(bem("Circle"), bem("Circle", "orange"))} />

            <span>Higher</span>
            <div className={classnames(bem("Circle"), bem("Circle", "yellow"))} />

            <span>Moderate</span>
            <div className={classnames(bem("Circle"), bem("Circle", "green"))} />

            <span>Lower</span>
          </td>
        </tr>
        {rows}
      </tbody>
    );
  }
}
